// Restricted (c) Siemens Healthineers AG, 2025, All rights reserved.

import { Injectable } from '@angular/core';
import { BehaviorSubject } from 'rxjs';
import { SystemDetailsModel } from '../../../api/remote-api/.portal.generated/models';

/**
 * used to store entries,
 * also used as primary item to render search results
 */
export interface SearchHistoryItem {
  utcDate: string;
  systemSerialNumber: string;
  systemMaterialNumber: number;
  systemName: string;
  customerName: string;
  customerLocation: string;
  bookmark?: boolean;
  // any new fields that are added MUST BE OPTIONAL for BACKWARD COMPATIBILITY
  modalityId?: number;
  functionalLocation?: string;
  equipmentTechId?: string | null;
  equipmentId?: string | null;
  unsupported?: boolean;
}

export type SearchHistory = SearchHistoryItem[];

@Injectable({
  providedIn: 'root'
})
export class SearchHistoryService {
  history$ = new BehaviorSubject<SearchHistory>([]);

  constructor() {
    getHistory().then((history) => {
      this.history$.next(history);
    });
  }

  /**
   * clears entire hisory
   */
  async clear(): Promise<void> {
    this.history$.next(await clearHistory());
  }

  /**
   * adds one item to history
   */
  async addItem(system: SystemDetailsModel): Promise<void> {
    const item: SearchHistoryItem = {
      utcDate: new Date().toUTCString(),
      systemSerialNumber: system.systemSerialNumber,
      systemMaterialNumber: system.systemMaterialNumber,
      systemName: system.systemName,
      customerLocation: system.customerLocation,
      customerName: system.customerName,
      functionalLocation: system.functionalLocation,
      equipmentTechId: system.equipmentTechId,
      equipmentId: system.equipmentId,
      modalityId: system.modalityId,
      unsupported: !system.isSeSOSupported,
    };
    this.history$.next(await storeItem(item));
  }

  /**
   * change bookmark state of item
   */
  async bookmarkItem(
    system: SystemDetailsModel,
    bookmark = true
  ): Promise<void> {
    const item: SearchHistoryItem = {
      utcDate: new Date().toUTCString(),
      systemSerialNumber: system.systemSerialNumber,
      systemMaterialNumber: system.systemMaterialNumber,
      systemName: system.systemName,
      customerLocation: system.customerLocation,
      customerName: system.customerName,
      modalityId: system.modalityId,
      functionalLocation: system.functionalLocation,
      equipmentTechId: system.equipmentTechId,
      equipmentId: system.equipmentId,
      bookmark
    };
    this.history$.next(await updateItem(item));
  }
}

/**
 * store and load methods
 * needs to make sure history is sorted and items are unique etc..
 */
const storageKey = 'seso_searchHistory';
const storageMaxItems = 50;
async function storeItem(item: SearchHistoryItem): Promise<SearchHistory> {
  // load history. in case anything goes wrong create an empty one
  let history = await getHistory();
  // in case the item was already in the history: remember previous state and remove it!
  const prevItem =
    history.find(
      ({ systemSerialNumber, systemMaterialNumber }) =>
        systemSerialNumber == item.systemSerialNumber &&
        systemMaterialNumber == item.systemMaterialNumber
    ) || {};
  history = history.filter(
    ({ systemSerialNumber, systemMaterialNumber }) =>
      systemSerialNumber != item.systemSerialNumber ||
      systemMaterialNumber != item.systemMaterialNumber
  );
  // add new item to top
  history.unshift({
    ...prevItem,
    ...item
  });
  // truncate history
  if (history.length > storageMaxItems) {
    history.splice(storageMaxItems, history.length);
  }
  // store history
  localStorage.setItem(storageKey, JSON.stringify(history));
  return history;
}

async function updateItem(item: SearchHistoryItem): Promise<SearchHistory> {
  // load history. in case anything goes wrong create an empty one
  let history = await getHistory();
  const item_: SearchHistoryItem | undefined = history.find(
    ({ systemSerialNumber, systemMaterialNumber }) =>
      systemSerialNumber == item.systemSerialNumber &&
      systemMaterialNumber == item.systemMaterialNumber
  );
  if (item_) {
    for (const k in item) {
      (item_ as Record<keyof SearchHistoryItem, unknown>)[
        k as keyof SearchHistoryItem
      ] = item[k as keyof SearchHistoryItem];
    }
  } else {
    // if it is a new item: insert it!
    history = await storeItem(item);
  }
  // store history
  localStorage.setItem(storageKey, JSON.stringify(history));
  return history;
}

async function getHistory(): Promise<SearchHistory> {
  // load history. in case anything goes wrong create an empty one
  const history = (() => {
    try {
      return JSON.parse(localStorage.getItem(storageKey) || 'false') || [];
    } catch {
      return [];
    }
  })();
  return history;
}

async function clearHistory(): Promise<SearchHistory> {
  // store history
  localStorage.setItem(storageKey, JSON.stringify([]));
  return [];
}
